import { useCallback, useEffect, useRef, useState } from "react";
import { fetchSchedule, normalizeSchedule } from "@/lib/api";

const POLL_MS = 45_000;

export default function useSchedule() {
  const [schedule, setSchedule] = useState(null);
  const [statusAvailable, setStatusAvailable] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const mountedRef = useRef(true);
  const requestRef = useRef(false);
  const abortRef = useRef(null);
  const responseGenerationRef = useRef(0);

  const refresh = useCallback(async () => {
    if (requestRef.current) return null;
    requestRef.current = true;
    const generation = ++responseGenerationRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const next = normalizeSchedule(await fetchSchedule(controller.signal));
      if (!next) throw new Error("Malformed schedule response");
      if (mountedRef.current && generation === responseGenerationRef.current) {
        setSchedule(next);
        setStatusAvailable(true);
      }
      return next;
    } catch {
      if (mountedRef.current && generation === responseGenerationRef.current) setStatusAvailable(false);
      return null;
    } finally {
      requestRef.current = false;
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const poll = setInterval(refresh, POLL_MS);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
      clearInterval(poll);
      clearInterval(clock);
    };
  }, [refresh]);

  return { schedule, statusAvailable, now, refresh };
}
